/**
 * Shipping Calculator
 *
 * Architecture:
 *  - LocalStorageManager : TTL-aware localStorage wrapper (module-level singleton)
 *  - Renderer            : pure DOM mutation functions (module-level, no state)
 *  - ShippingCalculator  : Component subclass — one instance per block root
 *      #config              : BlockConfig read from data-* attributes
 *      #refs                : scoped element references, built in connectedCallback
 *      #watchCountryOptions : re-cleans the country <select> whenever the Section
 *                             Rendering API rewrites it back to raw markup (cart updates)
 *      #fetchAndRender()    : orchestrates the interactive rate-fetch flow
 *      #silentFetch()       : background fetch on page load (updates trigger summary only)
 *      #softReset()         : hides results, returns to form, preserves field values
 *      #fullReset()         : clears everything including localStorage
 *
 * Rate fetch strategy:
 *  - Uses the Shopify cart shipping rates endpoint:
 *      GET /cart/shipping_rates.json — returns rates directly
 *  - On product pages a temporary cart cookie is used for the rate lookup.
 *  - On cart pages the real cart is used directly — no cookie swap.
 *
 * On-load silent fetch:
 *  - If localStorage has a prior address, rates are re-fetched silently on every
 *    page load (product or cart). Only the trigger-area summary is updated;
 *    the modal DOM is left untouched until the user opens it.
 *
 * Reset strategy:
 *  - Full reset  → "Clear" button. Wipes all form inputs, result views, errors,
 *                  and localStorage. Returns the calculator to factory-fresh state.
 *  - Soft reset  → "Change" button and dialog `close` event. Hides results pane,
 *                  returns to form, but preserves field values and localStorage.
 */

import { Component } from '@theme/component';

'use strict';

/* ═══════════════════════════════════════════════════════════
   LocalStorageManager
   TTL-aware localStorage helper. Stores only { country, province, zip }.
   lowestRate is intentionally excluded — rates are always recalculated
   fresh on page load because they differ between product and cart contexts.
═══════════════════════════════════════════════════════════ */

const LocalStorageManager = (() => {
  const NS        = 'sc_';
  const KEY       = `${NS}shipping_address`;
  const TTL_HOURS = 6;

  /** @param {unknown} value */
  function set(value) {
    try {
      localStorage.setItem(KEY, JSON.stringify({
        v:       value,
        expires: Date.now() + TTL_HOURS * 3_600_000,
      }));
    } catch (_) {}
  }

  /** @returns {{ country: string; province: string; zip: string } | null} */
  function get() {
    try {
      const raw = localStorage.getItem(KEY);
      if (!raw) return null;
      const payload = JSON.parse(raw);
      if (!payload || typeof payload.expires !== 'number') return null;
      if (Date.now() > payload.expires) { localStorage.removeItem(KEY); return null; }
      return payload.v ?? null;
    } catch (_) { return null; }
  }

  function remove() {
    try { localStorage.removeItem(KEY); } catch (_) {}
  }

  return {
    /** @param {{ country: string; province: string; zip: string }} data */
    saveAddress(data) { set(data); },

    /** @returns {{ country: string; province: string; zip: string } | null} */
    getAddress() {
      const data = get();
      if (
        data &&
        typeof data === 'object' &&
        typeof (/** @type {any} */ (data)).country === 'string' &&
        typeof (/** @type {any} */ (data)).zip     === 'string'
      ) return /** @type {any} */ (data);
      return null;
    },

    clearAddress() { remove(); },
  };
})();


/* ═══════════════════════════════════════════════════════════
   Types
═══════════════════════════════════════════════════════════ */

/**
 * @typedef {{
 *   country:  string;
 *   province: string;
 *   zip:      string;
 * }} StoredAddress
 *
 * @typedef {{
 *   showCountry:        boolean;
 *   showProvince:       boolean;
 *   showZip:            boolean;
 *   showPrice:          boolean;
 *   rateSummaryPrefix:  string | undefined;
 *   submitButtonLabel:  string | undefined;
 *   template:           string | undefined;
 * }} BlockConfig
 *
 * @typedef {{
 *   name:           string;
 *   price:          string;
 *   delivery_date?: string;
 * }} ShippingRate
 *
 * @typedef {{
 *   dialogEl:            HTMLDialogElement;
 *   triggerRowEl:        HTMLElement;
 *   rateSummaryRowEl:    HTMLElement;
 *   rateSummaryTextEl:   HTMLElement;
 *   countryEl:           HTMLSelectElement;
 *   provinceEl:          HTMLSelectElement;
 *   zipEl:               HTMLInputElement;
 *   submitBtn:           HTMLButtonElement;
 *   clearBtn:            HTMLButtonElement | null;
 *   errorEl:             HTMLElement;
 *   errorMsgEl:          HTMLElement;
 *   resultsWrapperEl:    HTMLElement;
 *   resultsEl:           HTMLElement;
 *   addressContextEl:    HTMLElement;
 *   addressPillEl:       HTMLElement;
 *   addressTextEl:       HTMLElement;
 *   changeBtn:           HTMLButtonElement | null;
 *   announcerEl:         HTMLElement;
 *   ratesListEl:         HTMLElement;
 *   noResultsEl:         HTMLElement;
 *   formEl:              HTMLElement;
 * }} DomRefs
 */


/* ═══════════════════════════════════════════════════════════
   DOM helpers
═══════════════════════════════════════════════════════════ */

/**
 * @template {Element} T
 * @param {string}            selector
 * @param {Element | Document} root
 * @returns {T | null}
 */
function qs(selector, root) {
  return /** @type {T | null} */ (root.querySelector(selector));
}

/** @param {unknown} value @returns {string} */
function escHtml(value) {
  return String(value)
    .replace(/&/g,  '&amp;')
    .replace(/</g,  '&lt;')
    .replace(/>/g,  '&gt;')
    .replace(/"/g,  '&quot;');
}


/* ═══════════════════════════════════════════════════════════
   Formatting
═══════════════════════════════════════════════════════════ */

/**
 * @param {string} amount
 * @param {{ asFree?: boolean }} [opts]
 * @returns {string}
 */
function formatPrice(amount, { asFree = true } = {}) {
  const n = parseFloat(amount || '0');
  if (Number.isNaN(n)) return escHtml(amount);
  if (n === 0 && asFree) return 'Free';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n);
}

/**
 * @param {ShippingRate[]} rates
 * @returns {ShippingRate}
 */
function getLowestRate(rates) {
  return /** @type {ShippingRate} */ (
    [...rates].sort((a, b) => parseFloat(a.price) - parseFloat(b.price))[0]
  );
}

/**
 * @param {string | number | Date} isoDate
 * @returns {string}
 */
function formatDeliveryDate(isoDate) {
  return new Date(isoDate).toLocaleDateString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
  });
}

/**
 * @param {StoredAddress} address
 * @param {ShippingRate[]} rates
 * @param {BlockConfig} config
 * @returns {string}
 */
function buildRateSummaryText(address, rates, config) {
  const addressParts = [
    config.showZip      && address.zip,
    config.showProvince && address.province,
    config.showCountry  && address.country,
  ].filter(Boolean).join(', ');

  let pricePart = '';
  if (config.showPrice && rates.length) {
    const lowest = getLowestRate(rates);
    pricePart = ` — from ${formatPrice(lowest.price, { asFree: false })} (${lowest.name})`;
  }

  return `${config.rateSummaryPrefix} to ${addressParts}${pricePart}`;
}

/**
 * @param {StoredAddress} address
 * @returns {string}
 */
function buildAddressPillText(address) {
  const parts = [address.zip, address.province, address.country].filter(Boolean).join(', ');
  return `Shipping to ${parts}`;
}


/* ═══════════════════════════════════════════════════════════
   Province select
═══════════════════════════════════════════════════════════ */

/** @param {HTMLSelectElement} countryEl */
function cleanCountryOptions(countryEl) {
  const seen = new Set();
  Array.from(countryEl.options).forEach((opt) => {
    if (!opt.value || opt.value === '---') { opt.remove(); return; }
    if (seen.has(opt.value))               { opt.remove(); return; }
    seen.add(opt.value);
  });
}

/**
 * @param {HTMLSelectElement} countryEl
 * @param {HTMLSelectElement} provinceEl
 */
function setupProvinceSelect(countryEl, provinceEl) {
  const provinceField = /** @type {HTMLElement | null} */ (
    provinceEl.closest('[data-field="province"]')
  );

  function populate() {
    // A selectedIndex of -1 means nothing is actually selected (e.g. a stray
    // change event landing on a stale/invalid selection) — not the same as
    // "this country has no provinces". Leave the field as-is rather than
    // wiping it.
    if (countryEl.selectedIndex === -1) return;

    const raw = countryEl.options[countryEl.selectedIndex]?.getAttribute('data-provinces') ?? '[]';
    /** @type {[string, string][]} */
    let provinces = [];
    try { provinces = JSON.parse(raw); } catch (_) {}

    const hasProvinces = Array.isArray(provinces) && provinces.length > 0;

    provinceEl.innerHTML = '';
    if (hasProvinces) {
      provinceEl.add(new Option('Select a state / province', ''));
      provinces.forEach(([value, label]) => provinceEl.add(new Option(label, value)));
    }
    provinceEl.disabled = !hasProvinces;
    provinceField?.classList.toggle('sc__field--hidden', !hasProvinces);
  }

  populate();
  countryEl.addEventListener('change', populate);
}


/* ═══════════════════════════════════════════════════════════
   Cart & product helpers
═══════════════════════════════════════════════════════════ */

/** @param {string} name @returns {string | null} */
function getCookie(name) {
  const escaped = name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const match   = document.cookie.match(new RegExp('(?:^|;\\s*)' + escaped + '=([^;]*)'));
  return match?.[1] != null ? decodeURIComponent(match[1]) : null;
}

/**
 * @param {string}           name
 * @param {string | number | boolean} value
 * @param {number}           [days=14]
 */
function setCookie(name, value, days = 14) {
  const expires = new Date(Date.now() + days * 86_400_000).toUTCString();
  document.cookie = `${name}=${encodeURIComponent(value)}; expires=${expires}; path=/; SameSite=Lax`;
}

/** @param {string} name */
function deleteCookie(name) {
  document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; SameSite=Lax`;
}

/** @returns {string} */
function generateTempCartToken() {
  const uid  = /** @type {any} */ (window).ShopifyAnalytics?.meta?.page?.customerId ?? 'g';
  const rand = crypto.getRandomValues(new Uint32Array(2))
    .reduce((acc, n) => acc + n.toString(36), '');
  return `tmp_${Date.now()}_${rand}_${uid}`.slice(0, 32);
}

/** @returns {number | null} */
function getActiveVariantId() {
  const input  = /** @type {HTMLInputElement | null} */ (
    document.querySelector('form[action="/cart/add"] [name="id"]')
  );
  const parsed = parseInt(input?.value ?? '', 10);
  return Number.isFinite(parsed) ? parsed : null;
}


/* ═══════════════════════════════════════════════════════════
   Validation
═══════════════════════════════════════════════════════════ */

const ErrorMessage = /** @type {const} */ ({
  MISSING_VARIANT:  'Please select a product option before checking rates.',
  MISSING_PROVINCE: 'Please select a state or province.',
  MISSING_ZIP:      'Please enter a ZIP or postal code.',
  FETCH_VARIANT:    'Could not load this variant. Please refresh and try again.',
  FETCH_RATES:      'Could not retrieve shipping rates. Please try again.',
  INVALID_PAGE:     'This feature can only be used on a product or cart page.',
  GENERAL:          'Something went wrong. Please try again.',
});

/**
 * @param {number | null} variantId
 * @param {string}        province
 * @param {string}        zip
 * @param {BlockConfig}   config
 * @returns {string | null}
 */
function validate(variantId, province, zip, config) {
  if (!['cart', 'product'].includes(config.template || '')) return ErrorMessage.INVALID_PAGE;
  if (config.template === 'product' && !variantId)           return ErrorMessage.MISSING_VARIANT;
  if (config.showProvince && !province)                       return ErrorMessage.MISSING_PROVINCE;
  if (config.showZip      && !zip)                            return ErrorMessage.MISSING_ZIP;
  return null;
}


/* ═══════════════════════════════════════════════════════════
   API helpers
═══════════════════════════════════════════════════════════ */

/**
 * @param {string} country
 * @param {string} province
 * @param {string} zip
 * @returns {Promise<ShippingRate[]>}
 */
async function fetchShippingRates(country, province, zip) {
  const params = new URLSearchParams({
    'shipping_address[country]':  country,
    'shipping_address[province]': province,
    'shipping_address[zip]':      zip,
  });

  const res = await fetch(`${Theme.routes.cart_url}/shipping_rates.json?${params}`, {
    credentials: 'same-origin',
    headers:     { 'X-Requested-With': 'XMLHttpRequest' },
  });

  if (!res.ok) throw new Error(ErrorMessage.FETCH_RATES);

  const { shipping_rates: rates = [] } = await res.json();
  return /** @type {ShippingRate[]} */ (rates);
}

/**
 * @param {number} variantId
 * @returns {Promise<void>}
 */
async function addVariantToCart(variantId) {
  const res = await fetch(Theme.routes.cart_add_url, {
    method:      'POST',
    credentials: 'same-origin',
    headers: {
      'Content-Type':     'application/json',
      'X-Requested-With': 'XMLHttpRequest',
    },
    body: JSON.stringify({ id: variantId, quantity: 1 }),
  });
  if (!res.ok) throw new Error(ErrorMessage.FETCH_VARIANT);
}


/* ═══════════════════════════════════════════════════════════
   Renderer
   Pure DOM-mutation functions. Accept only elements they touch.
═══════════════════════════════════════════════════════════ */

const Renderer = {

  /** @param {HTMLElement} errorEl @param {HTMLElement} errorMsgEl @param {string} message */
  showError(errorEl, errorMsgEl, message) {
    errorMsgEl.textContent = message;
    errorEl.hidden         = false;
  },

  /** @param {HTMLElement} errorEl @param {HTMLElement} errorMsgEl */
  clearError(errorEl, errorMsgEl) {
    errorMsgEl.textContent = '';
    errorEl.hidden         = true;
  },

  /** @param {HTMLButtonElement} btn @param {string} label @param {boolean} loading */
  setSubmitState(btn, label, loading) {
    btn.disabled    = loading;
    btn.textContent = loading ? 'Checking…' : label;
    btn.setAttribute('aria-busy', String(loading));
  },

  /** @param {DomRefs} refs @param {ShippingRate[]} rates */
  renderRates(refs, rates) {
    if (!rates.length) {
      refs.noResultsEl.hidden      = false;
      refs.announcerEl.textContent = 'No shipping rates found for this address.';
      return;
    }

    refs.ratesListEl.innerHTML = rates.map((rate) => `
      <li class="sc__rate-item">
        <div class="sc__rate-row">
          <span class="sc__rate-name paragraph">${escHtml(rate.name)}</span>
          <span class="sc__rate-price h5">${formatPrice(rate.price)}</span>
        </div>
        ${rate.delivery_date
          ? `<span class="sc__rate-delivery h6">
               Estimated delivery: ${escHtml(formatDeliveryDate(rate.delivery_date))}
             </span>`
          : ''}
      </li>`
    ).join('');

    refs.resultsEl.hidden = false;
    refs.resultsEl.classList.add('sc__results--visible');
    refs.announcerEl.textContent =
      `${rates.length} shipping rate${rates.length > 1 ? 's' : ''} found.`;
  },

  /** @param {DomRefs} refs @param {StoredAddress} address */
  showAddressPill(refs, address) {
    refs.addressTextEl.textContent = buildAddressPillText(address);
    refs.addressContextEl.hidden   = false;
    refs.addressPillEl.hidden      = false;
    refs.resultsWrapperEl.hidden   = false;
    refs.formEl.hidden             = true;
  },

  /**
   * @param {HTMLElement} triggerRowEl
   * @param {HTMLElement} rateSummaryRowEl
   * @param {HTMLElement} rateSummaryTextEl
   * @param {string}      summaryText
   */
  showRateSummary(triggerRowEl, rateSummaryRowEl, rateSummaryTextEl, summaryText) {
    rateSummaryTextEl.textContent = summaryText;
    rateSummaryRowEl.hidden       = false;
    triggerRowEl.hidden           = true;
  },

  /** @param {HTMLElement} triggerRowEl @param {HTMLElement} rateSummaryRowEl */
  hideSummaryShowTrigger(triggerRowEl, rateSummaryRowEl) {
    triggerRowEl.hidden     = false;
    rateSummaryRowEl.hidden = true;
  },
};


/* ═══════════════════════════════════════════════════════════
   ShippingCalculator — custom element
═══════════════════════════════════════════════════════════ */

class ShippingCalculator extends Component {

  // ── Private fields ───────────────────────────────────────

  /** @type {string} */
  #blockId = '';

  /** @type {BlockConfig} */
  #config = {
    showCountry:       false,
    showProvince:      false,
    showZip:           false,
    showPrice:         false,
    rateSummaryPrefix: undefined,
    submitButtonLabel: undefined,
    template:          undefined,
  };

  /** @type {DomRefs | null} */
  #refs = null;

  /** @type {MutationObserver | null} */
  #countryOptionsObserver = null;

  // ── Lifecycle ────────────────────────────────────────────

  connectedCallback() {
    super.connectedCallback();

    this.#blockId = this.dataset.blockId ?? '';
    if (!this.#blockId) return;

    this.#config = this.#readConfig();

    const refs = this.#buildRefs();
    if (!refs) return;
    this.#refs = refs;

    this.#setup();
    this.#bindEvents();
    this.#silentFetch();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#countryOptionsObserver?.disconnect();
  }

  // ── Config ───────────────────────────────────────────────

  /** @returns {BlockConfig} */
  #readConfig() {
    const ds = this.dataset;
    return {
      showCountry:       ds.showCountry       === 'true',
      showProvince:      ds.showProvince      === 'true',
      showZip:           ds.showZip           === 'true',
      showPrice:         ds.showPrice         === 'true',
      rateSummaryPrefix: ds.rateSummaryPrefix?.trim(),
      submitButtonLabel: ds.submitButtonLabel?.trim(),
      template:          ds.template,
    };
  }

  // ── Refs ─────────────────────────────────────────────────

  /**
   * Builds scoped element references for this block instance.
   * @returns {DomRefs | null}
   */
  #buildRefs() {
    const id  = (/** @type {string} */ suffix) => `#sc-${suffix}-${this.#blockId}`;
    const sel = {
      dialog:          id('dialog'),
      triggerRow:      id('trigger'),
      rateSummaryRow:  id('rate-summary'),
      rateSummaryText: id('rate-summary-text'),
      country:         id('country'),
      province:        id('province'),
      zip:             id('zip'),
      submit:          id('submit'),
      clear:           id('clear'),
      error:           id('error'),
      errorMsg:        id('error-msg'),
      resultsWrapper:  id('results-wrapper'),
      results:         id('results'),
      addressContext:  id('address-context'),
      addressPill:     id('address-pill'),
      addressText:     id('address-text'),
      changeBtn:       id('change-btn'),
      announcer:       id('rates-announcement'),
      ratesList:       id('rates-list'),
      noResults:       id('no-results'),
      form:            id('form'),
    };

    const dialogEl = qs(sel.dialog, this);
    if (!dialogEl) return null;

    const countryEl  = qs(sel.country,  dialogEl);
    const provinceEl = qs(sel.province, dialogEl);
    const zipEl      = qs(sel.zip,      dialogEl);
    const submitBtn  = qs(sel.submit,   dialogEl);

    // Required elements — bail if any are missing
    if (!countryEl || !provinceEl || !zipEl || !submitBtn) return null;

    return {
      dialogEl:          /** @type {HTMLDialogElement}  */ (dialogEl),
      triggerRowEl:      /** @type {HTMLElement}         */ (qs(sel.triggerRow,      this)),
      rateSummaryRowEl:  /** @type {HTMLElement}         */ (qs(sel.rateSummaryRow,  this)),
      rateSummaryTextEl: /** @type {HTMLElement}         */ (qs(sel.rateSummaryText, this)),
      countryEl:         /** @type {HTMLSelectElement}   */ (countryEl),
      provinceEl:        /** @type {HTMLSelectElement}   */ (provinceEl),
      zipEl:             /** @type {HTMLInputElement}    */ (zipEl),
      submitBtn:         /** @type {HTMLButtonElement}   */ (submitBtn),
      clearBtn:          /** @type {HTMLButtonElement | null} */ (qs(sel.clear,       dialogEl)),
      errorEl:           /** @type {HTMLElement}         */ (qs(sel.error,           dialogEl)),
      errorMsgEl:        /** @type {HTMLElement}         */ (qs(sel.errorMsg,        dialogEl)),
      resultsWrapperEl:  /** @type {HTMLElement}         */ (qs(sel.resultsWrapper,  dialogEl)),
      resultsEl:         /** @type {HTMLElement}         */ (qs(sel.results,         dialogEl)),
      addressContextEl:  /** @type {HTMLElement}         */ (qs(sel.addressContext,  dialogEl)),
      addressPillEl:     /** @type {HTMLElement}         */ (qs(sel.addressPill,     dialogEl)),
      addressTextEl:     /** @type {HTMLElement}         */ (qs(sel.addressText,     dialogEl)),
      changeBtn:         /** @type {HTMLButtonElement | null} */ (qs(sel.changeBtn,  dialogEl)),
      announcerEl:       /** @type {HTMLElement}         */ (qs(sel.announcer,       dialogEl)),
      ratesListEl:       /** @type {HTMLElement}         */ (qs(sel.ratesList,       dialogEl)),
      noResultsEl:       /** @type {HTMLElement}         */ (qs(sel.noResults,       dialogEl)),
      formEl:            /** @type {HTMLElement}         */ (qs(sel.form,            dialogEl)),
    };
  }

  // ── Setup ────────────────────────────────────────────────

  #setup() {
    const refs = /** @type {DomRefs} */ (this.#refs);
    cleanCountryOptions(refs.countryEl);
    this.#watchCountryOptions(refs.countryEl);
    setupProvinceSelect(refs.countryEl, refs.provinceEl);
    this.#prefillFromStorage();
  }

  /**
   * The cart page's `cart-items-component` morphs the whole `main-cart`
   * section — including this block — on every page load (a quantity
   * selector's `pageshow` handler re-fires its update event even without
   * user action) and on every quantity/line-item change. That full morph
   * writes the raw, uncleaned `country_option_tags` output straight back
   * into the country <select>, and empties the province <select> entirely
   * (it has no options in the server-rendered markup — it's built purely
   * client-side). Watch for that and re-clean + re-dispatch `change` so
   * `setupProvinceSelect`'s existing listener rebuilds the province list,
   * instead of depending on a lifecycle hook tied to the morph implementation.
   * @param {HTMLSelectElement} countryEl
   */
  #watchCountryOptions(countryEl) {
    const observer = new MutationObserver(() => {
      observer.disconnect();
      cleanCountryOptions(countryEl);
      countryEl.dispatchEvent(new Event('change'));
      observer.observe(countryEl, { childList: true });
    });
    observer.observe(countryEl, { childList: true });
    this.#countryOptionsObserver = observer;
  }

  // ── Events ───────────────────────────────────────────────

  #bindEvents() {
    const refs = /** @type {DomRefs} */ (this.#refs);

    refs.submitBtn.addEventListener('click', () => this.#fetchAndRender());
    refs.clearBtn?.addEventListener('click',  () => this.#fullReset());
    refs.changeBtn?.addEventListener('click', () => this.#softReset());

    refs.dialogEl.addEventListener('close', () => this.#softReset());

    [refs.countryEl, refs.provinceEl, refs.zipEl].forEach((el) => {
      el.addEventListener('change', () => {
        if (!refs.errorEl.hidden) Renderer.clearError(refs.errorEl, refs.errorMsgEl);
        if (el !== refs.zipEl)    refs.zipEl.value = '';
      });
    });
  }

  // ── Storage prefill ──────────────────────────────────────

  #prefillFromStorage() {
    const refs  = /** @type {DomRefs} */ (this.#refs);
    const saved = LocalStorageManager.getAddress();
    if (!saved) return;

    const { country, province, zip } = saved;
    const countryOption = Array.from(refs.countryEl.options).find((o) => o.value === country);

    if (!countryOption) { LocalStorageManager.clearAddress(); return; }

    refs.countryEl.value = country;
    refs.countryEl.dispatchEvent(new Event('change'));
    if (province) refs.provinceEl.value = province;
    if (zip)      refs.zipEl.value      = zip;
  }

  // ── Silent fetch ─────────────────────────────────────────
  // Runs on page load when storage has a prior address.
  // Updates the trigger-area summary only — never touches the modal.

  async #silentFetch() {
    const refs  = /** @type {DomRefs} */ (this.#refs);
    const saved = LocalStorageManager.getAddress();
    if (!saved) return;

    const { country, province, zip } = saved;
    if (!country || !zip) return;

    const isProductPage    = this.#config.template === 'product';
    const originalToken    = isProductPage ? getCookie('cart') : null;
    const restoreCart      = () => originalToken !== null
      ? setCookie('cart', originalToken)
      : deleteCookie('cart');
    const onBeforeUnload   = () => restoreCart();

    try {
      if (isProductPage) {
        const variantId = getActiveVariantId();
        if (!variantId) return;

        window.addEventListener('beforeunload', onBeforeUnload, { once: true });
        setCookie('cart', generateTempCartToken());
        await addVariantToCart(variantId);
      }

      const rates = await fetchShippingRates(country, province, zip);

      if (rates.length) {
        Renderer.showRateSummary(
          refs.triggerRowEl,
          refs.rateSummaryRowEl,
          refs.rateSummaryTextEl,
          buildRateSummaryText(saved, rates, this.#config)
        );
      }
    } catch (_) {
      // Silent — never surface background-fetch errors to the user
    } finally {
      if (isProductPage) restoreCart();
      window.removeEventListener('beforeunload', onBeforeUnload);
    }
  }

  // ── Interactive fetch ────────────────────────────────────

  async #fetchAndRender() {
    const refs   = /** @type {DomRefs} */ (this.#refs);
    const config = this.#config;

    const isProductPage     = config.template === 'product';
    const variantId         = isProductPage ? getActiveVariantId() : null;
    const country           = refs.countryEl.value.trim();
    const province          = refs.provinceEl.value.trim();
    const zip               = refs.zipEl.value.trim();
    const submitButtonLabel = config.submitButtonLabel
      ?? refs.submitBtn.textContent?.trim()
      ?? 'Submit';

    const validationError = validate(variantId, province, zip, config);
    if (validationError) {
      Renderer.showError(refs.errorEl, refs.errorMsgEl, validationError);
      return;
    }

    Renderer.clearError(refs.errorEl, refs.errorMsgEl);
    Renderer.setSubmitState(refs.submitBtn, submitButtonLabel, true);

    const originalToken  = isProductPage ? getCookie('cart') : null;
    const restoreCart    = () => originalToken !== null
      ? setCookie('cart', originalToken)
      : deleteCookie('cart');
    const onBeforeUnload = () => restoreCart();

    if (isProductPage) window.addEventListener('beforeunload', onBeforeUnload, { once: true });

    try {
      if (isProductPage) {
        setCookie('cart', generateTempCartToken());
        await addVariantToCart(/** @type {number} */ (variantId));
      }

      const rates = await fetchShippingRates(country, province, zip);

      /** @type {StoredAddress} */
      const address = { country, province, zip };

      Renderer.showAddressPill(refs, address);
      Renderer.renderRates(refs, rates);
      refs.dialogEl.scrollTo({ top: 0, behavior: 'smooth' });

      if (rates.length) {
        Renderer.showRateSummary(
          refs.triggerRowEl,
          refs.rateSummaryRowEl,
          refs.rateSummaryTextEl,
          buildRateSummaryText(address, rates, config)
        );
        LocalStorageManager.saveAddress(address);
      } else {
        Renderer.hideSummaryShowTrigger(refs.triggerRowEl, refs.rateSummaryRowEl);
        LocalStorageManager.clearAddress();
      }

    } catch (err) {
      const message = err instanceof Error ? err.message : ErrorMessage.GENERAL;
      Renderer.showError(refs.errorEl, refs.errorMsgEl, message);
    } finally {
      if (isProductPage) restoreCart();
      window.removeEventListener('beforeunload', onBeforeUnload);
      Renderer.setSubmitState(refs.submitBtn, submitButtonLabel, false);
    }
  }

  // ── Reset ────────────────────────────────────────────────

  /**
   * SOFT RESET — preserves field values and localStorage.
   * Triggered by the "Change" button and the dialog `close` event.
   */
  #softReset() {
    const refs = /** @type {DomRefs} */ (this.#refs);

    refs.resultsEl.hidden = true;
    refs.resultsEl.classList.remove('sc__results--visible');
    refs.noResultsEl.hidden      = true;
    refs.resultsWrapperEl.hidden = true;
    refs.announcerEl.textContent = '';
    refs.ratesListEl.innerHTML   = '';

    Renderer.clearError(refs.errorEl, refs.errorMsgEl);
    refs.formEl.hidden = false;
  }

  /**
   * FULL RESET — clears everything including localStorage.
   * Triggered by the "Clear" button.
   */
  #fullReset() {
    const refs = /** @type {DomRefs} */ (this.#refs);

    refs.countryEl.selectedIndex = 0;
    refs.countryEl.dispatchEvent(new Event('change'));
    refs.provinceEl.selectedIndex = 0;
    refs.zipEl.value              = '';

    refs.resultsEl.hidden = true;
    refs.resultsEl.classList.remove('sc__results--visible');
    refs.noResultsEl.hidden      = true;
    refs.resultsWrapperEl.hidden = true;
    refs.announcerEl.textContent = '';
    refs.ratesListEl.innerHTML   = '';

    Renderer.clearError(refs.errorEl, refs.errorMsgEl);
    refs.formEl.hidden = false;

    LocalStorageManager.clearAddress();
    Renderer.hideSummaryShowTrigger(refs.triggerRowEl, refs.rateSummaryRowEl);
  }
}

/* ═══════════════════════════════════════════════════════════
   Registration
═══════════════════════════════════════════════════════════ */

if (!customElements.get('shipping-calculator')) {
  customElements.define('shipping-calculator', ShippingCalculator);
}